export type PreparedRecoveryBackup = Readonly<{
  contents: string;
  jsonFile: File;
  textFile: File;
  /** Only a file type accepted by this browser's native share sheet. */
  shareFile?: File;
}>;

const prepareError = () =>
  new Error("Could not prepare this encrypted backup. Try exporting it again.");
const shareError = () =>
  new Error("Could not share the backup. Try downloading or copying it.");
const downloadError = () =>
  new Error("Could not start the download. Try sharing or copying the backup.");
const copyError = () =>
  new Error("Could not copy the backup. Try sharing or downloading it.");

// Accept only the established encrypted root-seed package. Never export an
// accidental passphrase, plaintext key, arbitrary extra field, or partial file.
const assertEncryptedBackup = (contents: string): void => {
  if (typeof contents !== "string" || contents.length > 16_384)
    throw prepareError();
  const value = JSON.parse(contents);
  if (
    !value ||
    Array.isArray(value) ||
    Object.keys(value).sort().join(",") !==
      "ciphertext,contextHash,iterations,kdf,nonce,salt,version,walletAddress" ||
    value.version !== 2 ||
    value.kdf !== "PBKDF2-SHA256" ||
    value.iterations !== 600_000 ||
    typeof value.walletAddress !== "string" ||
    value.walletAddress.length < 10 ||
    value.walletAddress.length > 128
  )
    throw prepareError();
  for (const [field, length] of [
    ["salt", 16],
    ["nonce", 12],
    ["ciphertext", 48],
    ["contextHash", 32],
  ] as const) {
    const encoded = value[field];
    if (typeof encoded !== "string") throw prepareError();
    const decoded = globalThis.atob(encoded);
    if (decoded.length !== length || globalThis.btoa(decoded) !== encoded)
      throw prepareError();
  }
};

/** Synchronous preparation only; no encryption, sharing, download, or storage. */
export const prepareRecoveryBackup = (
  contents: string
): PreparedRecoveryBackup => {
  try {
    assertEncryptedBackup(contents);
    const now = new Date();
    const basename = `redacted-private-recovery-${now
      .toISOString()
      .replace(/[:.]/g, "-")}`;
    const jsonFile = new File([contents], `${basename}.json`, {
      type: "application/json",
      lastModified: now.getTime(),
    });
    // Some mobile wallets reject application/json in Web Share. This is the
    // SAME encrypted JSON, merely carried as text/plain with a .txt extension.
    const textFile = new File([contents], `${basename}.txt`, {
      type: "text/plain",
      lastModified: now.getTime(),
    });
    let shareFile: File | undefined;
    const browser = globalThis.navigator;
    if (
      typeof browser?.share === "function" &&
      typeof browser.canShare === "function"
    ) {
      for (const file of [jsonFile, textFile]) {
        try {
          if (browser.canShare.call(browser, { files: [file] })) {
            shareFile = file;
            break;
          }
        } catch {
          // Rejection of one MIME type need not reject the encrypted-text file.
        }
      }
    }
    return Object.freeze({ contents, jsonFile, textFile, shareFile });
  } catch {
    throw prepareError();
  }
};

/** Call directly from a fresh click; no await may precede navigator.share. */
export const sharePreparedRecoveryBackup = (
  prepared: PreparedRecoveryBackup
): Promise<"shared" | "cancelled"> => {
  try {
    const browser = globalThis.navigator;
    if (!prepared.shareFile || typeof browser?.share !== "function")
      return Promise.reject(shareError());
    // Keep the native receiver AND transient user activation. Only the file is
    // shared, never the passphrase or a second text/URL payload.
    const sharing = browser.share.call(browser, {
      files: [prepared.shareFile],
    });
    return Promise.resolve(sharing).then(
      () => "shared" as const,
      (error: unknown) => {
        if (
          error &&
          typeof error === "object" &&
          "name" in error &&
          error.name === "AbortError"
        )
          return "cancelled" as const;
        throw shareError();
      }
    );
  } catch (error) {
    if (
      error &&
      typeof error === "object" &&
      "name" in error &&
      error.name === "AbortError"
    )
      return Promise.resolve("cancelled");
    return Promise.reject(shareError());
  }
};

/** Starts a download, but browsers cannot confirm that the user saved the file. */
export const downloadPreparedRecoveryBackup = (
  prepared: PreparedRecoveryBackup,
  format: "json" | "text" = "json"
): void => {
  let url: string | undefined;
  let anchor: HTMLAnchorElement | undefined;
  try {
    if (
      !globalThis.document?.body ||
      typeof URL.createObjectURL !== "function" ||
      typeof URL.revokeObjectURL !== "function" ||
      !["json", "text"].includes(format)
    )
      throw downloadError();
    const file = format === "text" ? prepared.textFile : prepared.jsonFile;
    url = URL.createObjectURL(file);
    anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = file.name;
    anchor.hidden = true;
    document.body.appendChild(anchor);
    anchor.click();
  } catch {
    throw downloadError();
  } finally {
    anchor?.remove();
    if (url) {
      const downloadUrl = url;
      // Revoking synchronously can cancel mobile downloads before they start.
      setTimeout(() => {
        try {
          URL.revokeObjectURL(downloadUrl);
        } catch {
          // Cleanup must not surface a delayed unhandled UI error.
        }
      }, 60_000);
    }
  }
};

/** Explicit user action only; never copy automatically after a failed share. */
export const copyEncryptedRecoveryBackup = (
  prepared: PreparedRecoveryBackup
): Promise<void> => {
  try {
    const clipboard = globalThis.navigator?.clipboard;
    if (typeof clipboard?.writeText !== "function")
      return Promise.reject(copyError());
    const copying = clipboard.writeText.call(clipboard, prepared.contents);
    return Promise.resolve(copying).catch(() => {
      throw copyError();
    });
  } catch {
    return Promise.reject(copyError());
  }
};
